/** Resuelve URL de media Food Nest (`/api/v1/...` → `/food-api/...`). */
import foodLogoMark from '../iconosFoodPlan/food-logo-mark.png';
import { formatFoodQuantity } from './foodPortionLabel';

export function resolveFoodMediaUrl(url: string): string {
  const s = String(url || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s) || s.startsWith('data:')) return s;
  const base = (import.meta.env.VITE_API_URL || '/food-api').replace(/\/$/, '');
  if (s.startsWith('/api/v1/')) return `${base}${s.slice('/api/v1'.length)}`;
  if (s.startsWith('/food-api/')) return s;
  if (s.startsWith('/')) return `${base}${s}`;
  return `${base}/${s}`;
}

/** Absolute URL usable from blob:/print documents. */
export function absoluteFoodMediaUrl(url: string): string {
  const resolved = resolveFoodMediaUrl(url);
  if (!resolved) return '';
  if (/^https?:\/\//i.test(resolved) || resolved.startsWith('data:')) return resolved;
  try {
    return new URL(resolved, window.location.origin).href;
  } catch {
    return resolved;
  }
}

/** URL absoluta del logo (blob:/print necesitan origen absoluto). */
function foodPlanLogoAbsoluteUrl(): string {
  const raw = String(foodLogoMark || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw) || raw.startsWith('data:')) return raw;
  if (raw.startsWith('/')) {
    try {
      return `${window.location.origin}${raw}`;
    } catch {
      return raw;
    }
  }
  try {
    return new URL(raw, window.location.href).href;
  } catch {
    return raw;
  }
}

export type UsualMealLike = {
  id?: string;
  name?: string;
  mealType?: string;
  meal_type?: string;
  items?: any[];
  totalCalories?: number;
  total_calories?: number;
  totalProtein?: number;
  total_protein?: number;
  totalCarbs?: number;
  total_carbs?: number;
  totalFat?: number;
  total_fat?: number;
  ingredientsText?: string;
  ingredients_text?: string;
  cookTimeMinutes?: number | null;
  cook_time_minutes?: number | null;
  photoUrls?: string[];
  photo_urls?: string[];
  notes?: string;
};

export function formatItemMacroLine(it: any): string {
  const name = it?.name || it?.foodName || it?.food_name || 'Alimento';
  const qty = formatFoodQuantity(
    it?.quantityGrams ?? it?.quantity_grams ?? it?.quantity ?? 0,
    it?.unit,
  );
  const kcal = Math.round(Number(it?.calories ?? it?.kcal ?? 0));
  const p = Math.round(Number(it?.protein ?? 0) * 10) / 10;
  const c = Math.round(Number(it?.carbs ?? 0) * 10) / 10;
  const f = Math.round(Number(it?.fat ?? 0) * 10) / 10;
  return `${name} — ${qty} · ${kcal} kcal · P ${p}g · C ${c}g · G ${f}g`;
}

export function formatMacroTotals(meal: {
  totalCalories?: number;
  total_calories?: number;
  totalProtein?: number;
  total_protein?: number;
  totalCarbs?: number;
  total_carbs?: number;
  totalFat?: number;
  total_fat?: number;
}): string {
  const kcal = Math.round(Number(meal.totalCalories ?? meal.total_calories ?? 0));
  const p = Math.round(Number(meal.totalProtein ?? meal.total_protein ?? 0) * 10) / 10;
  const c = Math.round(Number(meal.totalCarbs ?? meal.total_carbs ?? 0) * 10) / 10;
  const f = Math.round(Number(meal.totalFat ?? meal.total_fat ?? 0) * 10) / 10;
  return `${kcal} kcal · P ${p}g · C ${c}g · G ${f}g`;
}

function escapeHtml(s: string) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function normalizeUsualMeal(meal: UsualMealLike) {
  let photoUrls = meal.photoUrls ?? meal.photo_urls ?? [];
  if (typeof photoUrls === 'string') {
    try { photoUrls = JSON.parse(photoUrls); } catch { photoUrls = []; }
  }
  let items = meal.items ?? [];
  if (typeof items === 'string') {
    try { items = JSON.parse(items); } catch { items = []; }
  }
  return {
    name: meal.name || 'Comida habitual',
    mealType: meal.mealType || meal.meal_type || '—',
    items: Array.isArray(items) ? items : [],
    totalCalories: Number(meal.totalCalories ?? meal.total_calories ?? 0),
    totalProtein: Number(meal.totalProtein ?? meal.total_protein ?? 0),
    totalCarbs: Number(meal.totalCarbs ?? meal.total_carbs ?? 0),
    totalFat: Number(meal.totalFat ?? meal.total_fat ?? 0),
    ingredientsText: meal.ingredientsText ?? meal.ingredients_text ?? '',
    cookTimeMinutes: meal.cookTimeMinutes ?? meal.cook_time_minutes ?? null,
    photoUrls: Array.isArray(photoUrls) ? photoUrls : [],
    notes: meal.notes || '',
  };
}

/** Incrusta fotos como data: (blob:print no carga bien /food-api relativos). */
async function embedPhotoAsDataUrl(url: string): Promise<string> {
  const abs = absoluteFoodMediaUrl(url);
  if (!abs) return '';
  if (abs.startsWith('data:')) return abs;
  try {
    const res = await fetch(abs, { credentials: 'include', cache: 'force-cache' });
    if (!res.ok) return abs;
    const blob = await res.blob();
    if (!blob || blob.size === 0) return abs;
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || abs));
      reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
      reader.readAsDataURL(blob);
    });
  } catch {
    return abs;
  }
}

function buildUsualMealHtml(meal: UsualMealLike, photoSrcs: string[]) {
  const m = normalizeUsualMeal(meal);
  const logoSrc = foodPlanLogoAbsoluteUrl();
  const watermarkHtml = logoSrc
    ? `<img class="watermark" src="${escapeHtml(logoSrc)}" alt="" />`
    : '';
  const itemsHtml = m.items.length
    ? `<ul>${m.items.map((it: any) => `<li>${escapeHtml(formatItemMacroLine(it))}</li>`).join('')}</ul>`
    : '<p><em>Sin ítems nutricionales registrados</em></p>';

  const photosHtml = photoSrcs.length
    ? `<div class="photos">${photoSrcs.map((u) => `<img src="${escapeHtml(u)}" alt="" />`).join('')}</div>`
    : '';

  return `<!DOCTYPE html>
<html lang="es"><head><meta charset="utf-8"/>
<title>${escapeHtml(m.name)}</title>
<style>
  body{font-family:Segoe UI,Arial,sans-serif;color:#0f172a;max-width:720px;margin:24px auto;padding:0 16px;line-height:1.45;position:relative}
  .watermark{
    position:fixed;top:50%;left:50%;
    transform:translate(-50%,-50%) rotate(-28deg);
    width:min(420px,72vw);max-height:55vh;object-fit:contain;
    opacity:0.1;pointer-events:none;z-index:0;
    -webkit-print-color-adjust:exact;print-color-adjust:exact;
  }
  .sheet{position:relative;z-index:1}
  h1{font-size:1.4rem;margin:0 0 8px}
  .brand{font-size:0.75rem;letter-spacing:0.06em;text-transform:uppercase;color:#64ba30;margin:0 0 4px;font-weight:700}
  .meta{color:#475569;font-size:0.9rem;margin-bottom:16px}
  .box{border:1px solid #e2e8f0;border-radius:12px;padding:14px;margin:12px 0;background:rgba(255,255,255,0.92)}
  .box h2{font-size:0.95rem;margin:0 0 8px;color:#334155}
  .macros{display:flex;gap:12px;flex-wrap:wrap}
  .macros span{background:#f1f5f9;border-radius:8px;padding:6px 10px;font-size:0.85rem}
  .photos{display:flex;flex-wrap:wrap;gap:8px;margin-top:8px}
  .photos img{max-width:180px;max-height:140px;object-fit:cover;border-radius:8px;border:1px solid #e2e8f0}
  @media print{
    .no-print{display:none}
    .watermark{opacity:0.12}
    body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  }
</style></head><body>
  ${watermarkHtml}
  <div class="sheet">
  <p class="brand">FOOD PLAN</p>
  <h1>${escapeHtml(m.name)}</h1>
  <p class="meta">
    Tipo: ${escapeHtml(m.mealType)}
    ${m.cookTimeMinutes != null ? ` · Tiempo de cocción: ${Number(m.cookTimeMinutes)} min` : ''}
  </p>
  <div class="box"><h2>Macros</h2>
    <div class="macros">
      <span>${Math.round(m.totalCalories)} kcal</span>
      <span>P ${Math.round(m.totalProtein)}g</span>
      <span>C ${Math.round(m.totalCarbs)}g</span>
      <span>G ${Math.round(m.totalFat)}g</span>
    </div>
  </div>
  <div class="box"><h2>Ingredientes / preparación</h2>
    <p style="white-space:pre-wrap">${escapeHtml(m.ingredientsText || '—')}</p>
  </div>
  <div class="box"><h2>Alimentos (registro nutricional)</h2>${itemsHtml}</div>
  ${m.notes ? `<div class="box"><h2>Notas</h2><p style="white-space:pre-wrap">${escapeHtml(m.notes)}</p></div>` : ''}
  ${photosHtml ? `<div class="box"><h2>Fotos</h2>${photosHtml}</div>` : ''}
  <p class="no-print" style="margin-top:20px"><button type="button" onclick="window.print()">Imprimir / Guardar PDF</button></p>
  </div>
</body></html>`;
}

function whenDocumentImagesReady(doc: Document | null | undefined, timeoutMs = 4000): Promise<void> {
  if (!doc) return Promise.resolve();
  const imgs = Array.from(doc.images || []);
  if (!imgs.length) return Promise.resolve();
  return new Promise((resolve) => {
    let remaining = imgs.length;
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const tick = () => {
      remaining -= 1;
      if (remaining <= 0) done();
    };
    const timer = window.setTimeout(done, timeoutMs);
    for (const img of imgs) {
      if (img.complete && img.naturalWidth > 0) {
        tick();
        continue;
      }
      img.addEventListener('load', tick, { once: true });
      img.addEventListener('error', tick, { once: true });
    }
    // Si todas ya estaban complete, liberar timer.
    if (remaining <= 0) {
      window.clearTimeout(timer);
      done();
    }
  });
}

function printViaHiddenIframe(html: string): boolean {
  try {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'PDF comida habitual');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      iframe.remove();
      return false;
    }
    doc.open();
    doc.write(html);
    doc.close();
    const runPrint = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        setTimeout(() => { try { iframe.remove(); } catch { /* noop */ } }, 2000);
      }
    };
    void whenDocumentImagesReady(doc).then(runPrint);
    return true;
  } catch {
    return false;
  }
}

/**
 * Exporta PDF imprimible (diálogo Imprimir → Guardar como PDF).
 * Fotos se incrustan como data: — un documento blob: no carga /food-api relativos.
 */
export async function exportUsualMealPdf(meal: UsualMealLike): Promise<boolean> {
  const normalized = normalizeUsualMeal(meal);
  const photoSrcs = (
    await Promise.all(normalized.photoUrls.map((u) => embedPhotoAsDataUrl(u)))
  ).filter(Boolean);

  const html = buildUsualMealHtml(meal, photoSrcs);

  try {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    // Sin feature `noopener`: en Chrome devolvía null y el botón «fallaba».
    const w = window.open(url, '_blank');
    if (w) {
      try { w.opener = null; } catch { /* noop */ }
      let printed = false;
      const trigger = () => {
        if (printed) return;
        printed = true;
        try { w.focus(); w.print(); } catch { /* usuario puede usar el botón */ }
      };
      const arm = () => { void whenDocumentImagesReady(w.document).then(trigger); };
      arm();
      w.addEventListener('load', arm, { once: true });
      setTimeout(() => URL.revokeObjectURL(url), 120_000);
      return true;
    }
    URL.revokeObjectURL(url);
  } catch {
    /* fall through to iframe */
  }

  return printViaHiddenIframe(html);
}
